// SHIPPING-001 client: delivery profiles, zones (units + rates) and the presets.
// Shapes mirror packages/domain/shipping/src/http.ts (presentProfile).
import { useQuery } from "@tanstack/react-query";
import { request } from "../api/client";
import type { Page } from "../api/types";
import { opsKeys } from "../inventory/api";

export type UnitType = "country" | "province" | "geo_zone" | "district" | "local_level";
export type Service = "standard" | "express" | "pickup";

export interface ZoneUnit {
  type: UnitType;
  code: string;
  label?: string;
}
export interface Rate {
  service: Service;
  max_weight_g: number;
  fee_paisa: number;
}
export interface Zone {
  id: string;
  name: string;
  units: ZoneUnit[];
  cod_allowed: boolean;
  cod_max_paisa: number | null;
  free_over_paisa: number | null;
  eta_min_days: number | null;
  eta_max_days: number | null;
  rates: Rate[];
  active: boolean;
  position: number;
}
export interface Profile {
  id: string;
  store_id: string;
  name: string;
  is_default: boolean;
  version: number;
  zones: Zone[];
}

export type ZoneBody = Omit<Zone, "id" | "position" | "units"> & {
  units: { type: UnitType; code: string }[];
  position?: number;
};

export interface PresetBody {
  kind: "valley_outside" | "valley_only" | "all_nepal";
  valley_fee_paisa: number;
  outside_fee_paisa: number;
  valley_cod: boolean;
  outside_cod: boolean;
}

const base = (orgId: string, storeId: string) =>
  `/orgs/${encodeURIComponent(orgId)}/stores/${encodeURIComponent(storeId)}/delivery-profiles`;

export const shippingApi = {
  profiles: (orgId: string, storeId: string) => request<Page<Profile>>("GET", base(orgId, storeId)),
  createProfile: (
    orgId: string,
    storeId: string,
    body: { name: string; is_default?: boolean; preset?: PresetBody; zones?: ZoneBody[] },
    idempotencyKey: string,
  ) => request<Profile>("POST", base(orgId, storeId), { body, idempotencyKey }),
  addZone: (orgId: string, storeId: string, p: Profile, body: ZoneBody, idempotencyKey: string) =>
    request<Profile>("POST", `${base(orgId, storeId)}/${encodeURIComponent(p.id)}/zones`, {
      body,
      idempotencyKey,
      ifMatch: p.version,
    }),
  replaceZone: (orgId: string, storeId: string, p: Profile, zoneId: string, body: ZoneBody) =>
    request<Profile>(
      "PUT",
      `${base(orgId, storeId)}/${encodeURIComponent(p.id)}/zones/${encodeURIComponent(zoneId)}`,
      {
        body,
        ifMatch: p.version,
      },
    ),
  deleteZone: (orgId: string, storeId: string, p: Profile, zoneId: string) =>
    request<Profile>(
      "DELETE",
      `${base(orgId, storeId)}/${encodeURIComponent(p.id)}/zones/${encodeURIComponent(zoneId)}`,
      {
        ifMatch: p.version,
      },
    ),
  /** Name, default flag and (from the price-list editor) every zone at once. */
  updateProfile: (
    orgId: string,
    storeId: string,
    p: Profile,
    body: { name?: string; is_default?: true; zones?: ZoneBody[] },
  ) =>
    request<Profile>("PATCH", `${base(orgId, storeId)}/${encodeURIComponent(p.id)}`, {
      body,
      ifMatch: p.version,
    }),
  deleteProfile: (orgId: string, storeId: string, p: Profile) =>
    request<null>("DELETE", `${base(orgId, storeId)}/${encodeURIComponent(p.id)}`, {
      ifMatch: p.version,
    }),
  /** What checkout would offer for an address, weight and subtotal (the server quoter). */
  preview: (
    orgId: string,
    storeId: string,
    body: {
      address: { province: string; district: string; local_level: string; ward: number };
      weight_g: number;
      subtotal_paisa: number;
      profile_id?: string;
    },
  ) =>
    request<{ serviceable: boolean; options: QuoteOption[] }>(
      "POST",
      `/orgs/${encodeURIComponent(orgId)}/stores/${encodeURIComponent(storeId)}/delivery/preview`,
      { body },
    ),
};

/** "Use for all products": one bulk job over every product of the store (products.bulk). */
export const applyToAllProducts = (
  orgId: string,
  storeId: string,
  profileId: string,
  idempotencyKey: string,
) =>
  request<{ job_id: string; total: number; state: string }>(
    "POST",
    `/orgs/${encodeURIComponent(orgId)}/stores/${encodeURIComponent(storeId)}/products/bulk`,
    {
      body: {
        filter: {},
        action: "set_delivery_profile",
        params: { delivery_profile_id: profileId },
      },
      idempotencyKey,
    },
  );

export interface QuoteOption {
  service: string;
  label: string;
  fee_paisa: number;
  cod_allowed: boolean;
  cod_max_paisa: number | null;
  eta_days: { min: number; max: number } | null;
}

export const useProfiles = (orgId: string, storeId: string, enabled: boolean) =>
  useQuery({
    queryKey: opsKeys.profiles(orgId, storeId),
    queryFn: () => shippingApi.profiles(orgId, storeId),
    enabled,
  });

/** The zone's main fee: the catch-all standard bracket, else the smallest bracket. */
export function mainFee(z: Zone, service: Service = "standard"): number | null {
  const rates = z.rates.filter((r) => r.service === service);
  return (
    (
      rates.find((r) => r.max_weight_g === 0) ??
      [...rates].sort((a, b) => a.max_weight_g - b.max_weight_g)[0]
    )?.fee_paisa ?? null
  );
}
